import { FeePortfolioDetailDTO } from "@models/platform-analysis/entities/portfolioDetails";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { isAllExpandEssentialSubProductsHidden } from "@pages/platform-analysis-v2/_id/utils";
import { formatCurrencyWithFixedDecimals } from "@utils";
import { AnalysisSummaryPortfolioDetailsMessages as Messages } from "../../constants";
import { displayIntNumber } from "../../utils";

export const getAnalysisSummaryPortfolioDetailTableRows = ({
  data,
  selectedSubProducts,
  hiddenSubProductIds,
  portfolioLevel,
  showHoldingsNumber,
  forPdf = false,
}: {
  data?: FeePortfolioDetailDTO | null;
  selectedSubProducts?: FeeSubProductDTO[];
  hiddenSubProductIds?: string[];
  portfolioLevel?: FeeCalculatorPortfolioLevel;
  showHoldingsNumber?: boolean;
  forPdf?: boolean;
}): { label: string | JSX.Element; value: string }[] => {
  const result: { label: string | JSX.Element; value: string }[] = [];

  if (!data) return result;

  const {
    investments,
    retailInsuranceInvestments,
    transactionsOutside,
    transactionsWithin,
    cfsBalances,
    expandBalances,
    northBalances,
  } = getAnalysisSummaryPortfolioDetails(data);
  const { totalPortfolioValue, totalDifferentInvestments } = data || {};

  const showTotalPortfolioValue = !!totalPortfolioValue;
  if (showTotalPortfolioValue) {
    result.push({
      label: Messages.TotalPortfolioValue,
      value: formatCurrencyWithFixedDecimals({
        value: totalPortfolioValue,
      }),
    });
  }

  const showBalanceInCfs = cfsBalances.length > 0;
  if (showBalanceInCfs) {
    result.push({
      label: Messages.BalanceInCFSEdgeMenus,
      value: cfsBalances.join(", "),
    });
  }

  const isAllExpandEssentialsHidden = isAllExpandEssentialSubProductsHidden({
    selectedSubProducts: selectedSubProducts || [],
    hiddenSubProductIds: hiddenSubProductIds || [],
  });
  const showBalanceInExpand =
    !isAllExpandEssentialsHidden && expandBalances.length > 0;
  if (showBalanceInExpand) {
    result.push({
      label: Messages.BalanceInExpandEssentialMenus,
      value: expandBalances.join(", "),
    });
  }

  const showBalanceInNorth = northBalances.length > 0;
  if (showBalanceInNorth) {
    result.push({
      label: Messages.BalanceInNorthMenus,
      value: northBalances.join(", "),
    });
  }

  const showInvestmentRow =
    portfolioLevel !== FeeCalculatorPortfolioLevel.Family &&
    investments.length > 0;
  if (showInvestmentRow) {
    result.push({
      label: Messages.InvestmentsAndOtherHoldings,
      value: investments.join(", "),
    });
  }

  const showTotalDifferentInvestmentsHeld =
    showHoldingsNumber && !!totalDifferentInvestments;
  if (showTotalDifferentInvestmentsHeld) {
    result.push({
      label: Messages.TotalDifferentInvestmentsHeld,
      value: displayIntNumber(totalDifferentInvestments || 0),
    });
  }

  const showTransactionsOutside = transactionsOutside.length > 0;
  if (showTransactionsOutside) {
    result.push({
      label: forPdf
        ? Messages.ManageAccountTransactionOutsideHtmlPdf
        : Messages.ManageAccountTransactionOutsideHtml,
      value: transactionsOutside.join("; "),
    });
  }

  const showTransactionsWithin = transactionsWithin.length > 0;
  if (showTransactionsWithin) {
    result.push({
      label: forPdf
        ? Messages.ManageAccountTransactionWithinHtmlPdf
        : Messages.ManageAccountTransactionWithinHtml,
      value: transactionsWithin.join("; "),
    });
  }

  const showRetailInsuranceInvestments =
    portfolioLevel === FeeCalculatorPortfolioLevel.Family &&
    retailInsuranceInvestments.length > 0;
  if (showRetailInsuranceInvestments) {
    result.push({
      label: Messages.NumberOfRetailInsurancePolices,
      value: retailInsuranceInvestments.join("; "),
    });
  }

  return result;
};

export const getAnalysisSummaryPortfolioDetails = (
  data?: FeePortfolioDetailDTO | null,
) => {
  const result = {
    transactionsOutside: [] as string[],
    transactionsWithin: [] as string[],
    investments: [] as string[],
    retailInsuranceInvestments: [] as string[],
    cfsBalances: [] as string[],
    expandBalances: [] as string[],
    northBalances: [] as string[],
    cpsBalances: [] as string[],
  };

  if (!data) return result;

  // Process all investment types
  processCashBalances({ data, investments: result.investments });

  processManagedFunds({
    data,
    investments: result.investments,
    transactionsOutside: result.transactionsOutside,
  });

  processManagedAccounts({
    data,
    investments: result.investments,
    transactionsWithin: result.transactionsWithin,
  });

  processAustralianInvestments({
    data,
    investments: result.investments,
    transactionsOutside: result.transactionsOutside,
  });

  processInternationalInvestments({
    data,
    investments: result.investments,
    transactionsOutside: result.transactionsOutside,
  });

  processTermDeposits({ data, investments: result.investments });

  processRetailInsurance({
    data,
    investments: result.investments,
  });

  processRetailInsurance({
    data,
    investments: result.retailInsuranceInvestments,
  });

  processBonds({
    data,
    investments: result.investments,
    transactionsOutside: result.transactionsOutside,
  });

  // Process platform balances
  processCfsBalances({ data, cfsBalances: result.cfsBalances });
  processExpandBalances({ data, expandBalances: result.expandBalances });
  processNorthBalances({ data, northBalances: result.northBalances });
  processCPSBalances({ data, cpsBalances: result.cpsBalances });

  return result;
};

const addBalance = ({
  investments,
  balance,
  message,
}: {
  investments: string[];
  balance: number | undefined;
  message: string;
}): void => {
  if (!balance || balance <= 0) return;

  investments.push(
    formatCurrencyWithFixedDecimals({
      message,
      value: balance,
    }),
  );
};

// #region Private Methods
const addTransaction = ({
  transactions,
  count,
  average,
  type,
}: {
  transactions: string[];
  count: number | undefined;
  average: number | undefined;
  type: string;
}): void => {
  if (!count || count <= 0) return;

  transactions.push(
    `${type}: ${displayIntNumber(
      count,
    )} transactions with an average size of ${formatCurrencyWithFixedDecimals({
      value: average || 0,
    })} each`,
  );
};

const processCashBalances = ({
  data,
  investments,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
}): void => {
  addBalance({
    investments,
    balance: data.idpsCashBalance,
    message: Messages.CashInIdps,
  });

  addBalance({
    investments,
    balance: data.superCashBalance,
    message: Messages.CashInSuperPension,
  });
};

const processManagedFunds = ({
  data,
  investments,
  transactionsOutside,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
  transactionsOutside: string[];
}): void => {
  if (!data.isChooseMF) return;

  addBalance({
    investments,
    balance: data.mfBalanceI,
    message: Messages.ManagedFunds,
  });

  addTransaction({
    transactions: transactionsOutside,
    count: data.mfTransactions,
    average: data.mfAvg,
    type: Messages.ManagedFunds,
  });
};

const processManagedAccounts = ({
  data,
  investments,
  transactionsWithin,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
  transactionsWithin: string[];
}): void => {
  if (!data.isChooseMA) return;

  addBalance({
    investments,
    balance: data.maBalanceI,
    message: Messages.ManagedAccounts,
  });

  const transactionConfigs = [
    {
      count: data.maMfTransactions,
      avg: data.maMfAvg,
      type: Messages.ManagedFunds,
    },
    {
      count: data.maAuTransactions,
      avg: data.maAuAvg,
      type: Messages.AustralianListedInvestments,
    },
    {
      count: data.maIntlTransactions,
      avg: data.maIntlAvg,
      type: Messages.InternationalListedInvestments,
    },
    {
      count: data.maBondTransactions,
      avg: data.maBondAvg,
      type: Messages.UnlistedBonds,
    },
  ];

  transactionConfigs.forEach(({ count, avg, type }) => {
    addTransaction({
      transactions: transactionsWithin,
      count,
      average: avg,
      type,
    });
  });
};

const processAustralianInvestments = ({
  data,
  investments,
  transactionsOutside,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
  transactionsOutside: string[];
}): void => {
  if (!data.isChooseAU) return;

  addBalance({
    investments,
    balance: data.auBalanceI,
    message: Messages.AustralianListedInvestments,
  });

  addTransaction({
    transactions: transactionsOutside,
    count: data.auTransactions,
    average: data.auAvg,
    type: Messages.AustralianListedInvestments,
  });
};

const processInternationalInvestments = ({
  data,
  investments,
  transactionsOutside,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
  transactionsOutside: string[];
}): void => {
  if (!data.isChooseIntl) return;

  addBalance({
    investments,
    balance: data.intlBalanceI,
    message: Messages.InternationalListedInvestments,
  });

  addTransaction({
    transactions: transactionsOutside,
    count: data.intlTransactions,
    average: data.intlAvg,
    type: Messages.InternationalListedInvestments,
  });
};

const processTermDeposits = ({
  data,
  investments,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
}): void => {
  if (!data.isChooseTD) return;

  addBalance({
    investments,
    balance: data.tdBalanceI,
    message: Messages.TermDeposits,
  });
};

const processRetailInsurance = ({
  data,
  investments,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
}): void => {
  if (!data.isChooseRetIns) return;

  if (data.idpsNumRetIns && data.idpsNumRetIns > 0) {
    investments.push(
      `${Messages.RetailInsurancePoliciesInIDPS}: ${displayIntNumber(
        data.idpsNumRetIns,
      )}`,
    );
  }

  if (data.superNumRetIns && data.superNumRetIns > 0) {
    investments.push(
      `${Messages.RetailInsurancePoliciesInSuper}: ${displayIntNumber(
        data.superNumRetIns,
      )}`,
    );
  }
};

const processBonds = ({
  data,
  investments,
  transactionsOutside,
}: {
  data: FeePortfolioDetailDTO;
  investments: string[];
  transactionsOutside: string[];
}): void => {
  if (!data.isChooseBond) return;

  addBalance({
    investments,
    balance: data.bondBalanceI,
    message: Messages.UnlistedBonds,
  });

  addTransaction({
    transactions: transactionsOutside,
    count: data.bondTransactions,
    average: data.bondAvg,
    type: Messages.UnlistedBonds,
  });
};

const processCfsBalances = ({
  data,
  cfsBalances,
}: {
  data: FeePortfolioDetailDTO;
  cfsBalances: string[];
}): void => {
  const cfsConfigs = [
    { balance: data.edgeFull, message: Messages.CfsEdgeFullMenu },
    { balance: data.edgeA100, message: Messages.CfsEdgeAccelerate100SMAs },
    { balance: data.edgeA30, message: Messages.CfsEdgeAccelerate30SMAs },
    {
      balance: data.edgeEligible,
      message: Messages.CfsEdgeManagedFundsEligibleForNoAdminFees,
    },
  ];

  cfsConfigs.forEach(({ balance, message }) => {
    addBalance({ investments: cfsBalances, balance, message });
  });
};

const processExpandBalances = ({
  data,
  expandBalances,
}: {
  data: FeePortfolioDetailDTO;
  expandBalances: string[];
}): void => {
  const expandConfigs = [
    { balance: data.essential, message: Messages.ExpandEssential },
    { balance: data.essentialPlus, message: Messages.ExpandEssentialPlus },
  ];

  expandConfigs.forEach(({ balance, message }) => {
    addBalance({ investments: expandBalances, balance, message });
  });
};

const processNorthBalances = ({
  data,
  northBalances,
}: {
  data: FeePortfolioDetailDTO;
  northBalances: string[];
}): void => {
  const northConfigs = [
    { balance: data.northChoice, message: Messages.NorthChoice },
    { balance: data.northSelect, message: Messages.NorthSelect },
    { balance: data.northGrow, message: Messages.NorthGrow },
  ];

  northConfigs.forEach(({ balance, message }) => {
    addBalance({ investments: northBalances, balance, message });
  });
};

const processCPSBalances = ({
  data,
  cpsBalances,
}: {
  data: FeePortfolioDetailDTO;
  cpsBalances: string[];
}): void => {
  const cpsConfigs = [
    { balance: data.cpsCore, message: Messages.CPSCoreMenu },
    { balance: data.cpsFull, message: Messages.CPSFullMenu },
  ];

  cpsConfigs.forEach(({ balance, message }) => {
    addBalance({ investments: cpsBalances, balance, message });
  });
};
// #endregion Private Methods
